import { useAtomValue } from 'jotai';
import type { Feature, Polygon } from 'geojson';
import { CanopyProperties } from './types';
import { useGridCellLayer } from '../map/GridCellLayer';
import React from 'react';
import { canopyChunksSelector } from './selectors';
import { POSTProperties } from '../Post/types';
import { fetchFlatGeobuf } from '../services/featuresApi';

type Props = {
  extruded: boolean;
  visible: boolean;
  /**
   * Whether this mount needs the swath table at all. The hook is mounted by the
   * shared Map component on every route; `false` (incident views, where the
   * swaths never draw) skips the fetch instead of hiding its result.
   */
  enabled: boolean;
};

export const useCanopySwathLayer = (props: Props) => {
  const postData = useAtomValue(canopyChunksSelector);
  const [swathData, setSwathData] = React.useState<Feature<Polygon, CanopyProperties>[]>([]);

  const availableKmls = Array.from(
    new Set(
      postData?.flatMap(c =>
        c.features.map((f: Feature<Polygon, POSTProperties>) => f.properties.kml_id)
      ) ?? []
    )
  );

  const loaded = swathData.length > 0;

  React.useEffect(() => {
    // Fetched once per session for the views that draw the swaths: the whole
    // table is pulled, and the previous unconditional mount-effect refetched it
    // on every route change including incident views.
    if (!props.enabled || loaded) {
      return;
    }
    let cancelled = false;
    fetchFlatGeobuf<Polygon, CanopyProperties>('canopy_swaths')
      .then(fc => {
        if (!cancelled) setSwathData(fc.features);
      })
      .catch((error: unknown) => {
        console.error('Error fetching canopy swaths:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [props.enabled, loaded]);

  const filteredSwaths = React.useMemo(() => {
    if (availableKmls.length === 0 || swathData.length === 0) return [];
    return swathData.filter(f => availableKmls.includes(f.properties.kml_id));
  }, [swathData, availableKmls]);

  const swathCollection = React.useMemo(
    () =>
      filteredSwaths.length > 0
        ? [{ type: 'FeatureCollection' as const, features: filteredSwaths }]
        : [],
    [filteredSwaths]
  );

  const GridCellLayer = useGridCellLayer<Feature<Polygon, CanopyProperties>>({
    id: 'canopy-swath-post',
    // GridCellLayer internally handles both Feature[] and FeatureCollection[]
    data: swathCollection as unknown as Feature<Polygon, CanopyProperties>[],
    getFillColor: () => [0, 0, 0, 100],
    getLineColor: () => [181, 9, 9, 200],
    pickable: true,
    extruded: props.extruded,
    stroked: true,
    visible: props.visible,
  });

  return GridCellLayer;
};
